import { lazy, useEffect } from 'react'
import { BrowserRouter, MemoryRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material'
import { brandComponentOverrides } from '@/blyn/theme/brand-theme-overrides'
import { BRAND, SURFACE, TEXT, FONT } from '@/blyn/theme/brand-tokens'
import { ToastProvider, type ToastMessage } from '@/lib/toast'
import { SettingsProvider } from '@/lib/settings'
import { AuthProvider, SessionProvider, type AuthData, type SessionUser } from '@/lib/auth'
import { HeaderProvider } from '@/lib/header-context'
import BlynLayout from '@/pages/layout'
import { HelpDrawer } from '@/blyn/HelpDrawer'
import { store } from '@/blyn/legacy/state/store'

// Lazy-load every page for fast initial render
const ConversationPage     = lazy(() => import('@/pages/conversation/page'))
const NewConversationPage  = lazy(() => import('@/pages/new-conversation/page'))
const ProjectsPage         = lazy(() => import('@/pages/projects/page'))
const ProjectWorkspacePage = lazy(() => import('@/pages/project-workspace/page'))
const TasksPage            = lazy(() => import('@/pages/tasks/page'))
const StagedPage           = lazy(() => import('@/pages/staged/page'))
const ApprovalsPage        = lazy(() => import('@/pages/approvals/page'))
const CreativesPage        = lazy(() => import('@/pages/creatives/page'))
const KnowledgePage        = lazy(() => import('@/pages/knowledge/page'))
const ForecastPage         = lazy(() => import('@/pages/forecast/page'))
const NudgesPage           = lazy(() => import('@/pages/nudges/page'))
const NotificationsPage    = lazy(() => import('@/pages/notifications/page'))
const WebPersonalizationPage = lazy(() => import('@/pages/web-personalization/page'))
const ProductCatalogPage   = lazy(() => import('@/pages/product-catalog/page'))
const TrackRecordPage      = lazy(() => import('@/pages/track-record/page'))
const AuditLogPage         = lazy(() => import('@/pages/audit-log/page'))
const SettingsPage         = lazy(() => import('@/pages/settings/page'))
const ComposePage          = lazy(() => import('@/pages/compose/page'))
const FirstRunPage         = lazy(() => import('@/pages/first-run/page'))
const HelpPage             = lazy(() => import('@/pages/help/page'))
const SearchPage           = lazy(() => import('@/pages/search/page'))

export interface BylnAgentProps {
  /** 'browser' uses window.history; 'memory' is for embedding inside a host router */
  routerMode?: 'browser' | 'memory'
  /** MUI color mode */
  colorScheme?: 'light' | 'dark'
  /** Called whenever Blyn pushes a toast notification */
  onToast?: (toast: ToastMessage) => void
  /**
   * Called when the user shares a conversation or project. Blyn doesn't own
   * app routing when embedded (`routerMode: 'memory'`), so the host should
   * copy a real deep link. If omitted, Blyn copies a `/blyn/...` or
   * `/ai-agent/...` URL itself.
   */
  onShare?: (payload: { conversationId?: string; projectId?: string; title?: string }) => void
  /** Pass real auth data from the host app */
  auth?: Partial<AuthData>
  /** Pass real session user from the host app */
  user?: SessionUser | null
  /** Override the initial route (memory router only) — e.g. '/blyn/first-run' */
  initialRoute?: string
  /**
   * Called whenever Blyn's in-app route changes (pathname + search).
   * Hosts that embed with `routerMode: 'memory'` should map this onto a
   * real URL (e.g. `/blyn/projects` → `/ai-agent/projects`) so the page
   * is refreshable and shareable.
   */
  onRouteChange?: (path: string) => void
  /** Base URL of the host API, e.g. 'https://api.example.com'. Used for project PATCH calls. */
  apiBaseUrl?: string
}

function RouteSync({ onRouteChange }: { onRouteChange?: (path: string) => void }) {
  const { pathname, search } = useLocation()
  const path = `${pathname}${search}`

  useEffect(() => {
    onRouteChange?.(path)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('blyn:route-change', { detail: { path } }))
    }
  }, [path, onRouteChange])

  return null
}

function BlynRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to={localStorage.getItem('blyn-onboarded') ? '/blyn/new-conversation' : '/blyn/first-run'} replace />} />
      {/* Single shared layout — stays mounted across all navigations */}
      <Route element={<BlynLayout />}>
          <Route path="/blyn"                        element={<Navigate to={localStorage.getItem('blyn-onboarded') ? '/blyn/new-conversation' : '/blyn/first-run'} replace />} />
          <Route path="/blyn/conversation"           element={<ConversationPage />} />
          <Route path="/blyn/new-conversation"       element={<NewConversationPage />} />
          <Route path="/blyn/projects"               element={<ProjectsPage />} />
          <Route path="/blyn/project-workspace"      element={<ProjectWorkspacePage />} />
          <Route path="/blyn/tasks"                  element={<TasksPage />} />
          <Route path="/blyn/staged"                 element={<StagedPage />} />
          <Route path="/blyn/approvals"              element={<ApprovalsPage />} />
          <Route path="/blyn/creatives"              element={<CreativesPage />} />
          <Route path="/blyn/knowledge"              element={<KnowledgePage />} />
          <Route path="/blyn/forecast"               element={<ForecastPage />} />
          <Route path="/blyn/nudges"                 element={<NudgesPage />} />
          <Route path="/blyn/notifications"          element={<NotificationsPage />} />
          <Route path="/blyn/web-personalization"    element={<WebPersonalizationPage />} />
          <Route path="/blyn/product-catalog"        element={<ProductCatalogPage />} />
          <Route path="/blyn/track-record"           element={<TrackRecordPage />} />
          <Route path="/blyn/audit-log"              element={<AuditLogPage />} />
          <Route path="/blyn/settings"               element={<SettingsPage />} />
          <Route path="/blyn/compose"                element={<ComposePage />} />
          <Route path="/blyn/first-run"              element={<FirstRunPage />} />
          <Route path="/blyn/help"                   element={<HelpPage />} />
          <Route path="/blyn/search"                 element={<SearchPage />} />
          <Route path="*"                            element={<Navigate to="/blyn/new-conversation" replace />} />
        </Route>
      </Routes>
  )
}

export default function BylnAgent({
  routerMode = 'browser',
  colorScheme = 'dark',
  onToast,
  auth,
  user,
  initialRoute,
  apiBaseUrl,
  onShare,
  onRouteChange,
}: BylnAgentProps) {
  useEffect(() => {
    if (apiBaseUrl) store.set({ apiBaseUrl })
  }, [apiBaseUrl])

  useEffect(() => {
    store.set({ routerMode, onShareCallback: onShare ?? null })
  }, [routerMode, onShare])

  const isDark = colorScheme === 'dark'
  const theme = createTheme({
    typography: {
      fontFamily: FONT.body,
    },
    palette: {
      mode: colorScheme,
      primary:    { main: BRAND.teal, light: BRAND.light, dark: BRAND.navy },
      background: {
        default: isDark ? SURFACE.dark.app : SURFACE.light.app,
        paper:   isDark ? SURFACE.dark.canvas : SURFACE.light.canvas,
      },
      text: {
        primary:   isDark ? TEXT.dark.primary : TEXT.light.primary,
        secondary: isDark ? TEXT.dark.secondary : TEXT.light.secondary,
      },
    },
    components: {
      ...brandComponentOverrides,
      MuiModal: { defaultProps: { disableScrollLock: true } },
    },
  })
  const Router = routerMode === 'memory' ? MemoryRouter : BrowserRouter
  const isOnboarded = !!localStorage.getItem('blyn-onboarded')
  const savedRoute = routerMode === 'memory' ? (sessionStorage.getItem('blyn-last-route') ?? undefined) : undefined
  // If user hasn't completed onboarding, always start at first-run regardless of saved route
  const resolvedInitialRoute = initialRoute ?? (isOnboarded ? savedRoute : '/blyn/first-run')
  const routerProps = routerMode === 'memory' && resolvedInitialRoute ? { initialEntries: [resolvedInitialRoute] } : {}

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider auth={auth}>
        <SessionProvider user={user}>
          <SettingsProvider defaultColorScheme={colorScheme}>
            <ToastProvider onToast={onToast}>
              <HeaderProvider>
                <Router {...routerProps}>
                  <RouteSync onRouteChange={onRouteChange} />
                  <BlynRoutes />
                </Router>
                <HelpDrawer />
              </HeaderProvider>
            </ToastProvider>
          </SettingsProvider>
        </SessionProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}
